// src/pages/PermissionsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api/client";          // <-- central axios with baseURL=http://localhost:8000
import "../styles/PermissionsPage.css";

export default function PermissionsPage() {
  const [permissions, setPermissions] = useState([]);
  const [form, setForm] = useState({ id: null, name: "", description: "" });
  const [viewItem, setViewItem] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const perPage = 5;

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const res = await api.get("/permissions/");
        setPermissions(res.data || []);
      } catch (e) {
        console.error(e);
        setErr(e?.response?.data?.detail || "Failed to load permissions");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => setPage(1), [search]);

  const refresh = async () => {
    const res = await api.get("/permissions/");
    setPermissions(res.data || []);
  };

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    const payload = { name: form.name.trim(), description: form.description?.trim() || "" };

    try {
      if (form.id) {
        await api.put(`/permissions/${form.id}`, payload);
      } else {
        await api.post("/permissions/", payload);
      }
      await refresh();
      resetForm();
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (perm) =>
    setForm({ id: perm.id, name: perm.name || "", description: perm.description || "" });

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure to delete this permission?")) return;
    setErr("");
    try {
      await api.delete(`/permissions/${id}`);
      await refresh();
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.detail || "Delete failed");
    }
  };

  const resetForm = () => setForm({ id: null, name: "", description: "" });

  const filtered = useMemo(() => {
    const q = (search || "").toLowerCase();
    return (permissions || []).filter(
      (p) =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q)
    );
  }, [permissions, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  return (
    <div className="permissions-page" width="100%">
      <h2>{form.id ? "Edit Permission" : "Create Permission"}</h2>

      <form onSubmit={handleSubmit} className="form">
        <input
          name="name"
          placeholder="Name"
          value={form.name}
          onChange={handleChange}
          required
        />
        <input
          name="description"
          placeholder="Description"
          value={form.description}
          onChange={handleChange}
        />
        <button type="submit" disabled={saving}>
          {saving ? "Saving..." : form.id ? "Update" : "Create"}
        </button>
        {form.id && (
          <button type="button" onClick={resetForm}>
            Cancel
          </button>
        )}
      </form>

      {err && <div className="error-banner">{err}</div>}
      {loading && <div className="loading">Loading…</div>}

      <h3>Permission List</h3>
      <input
        className="search-box"
        placeholder="Search..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="table-container">
        <table className="perm-table">
          <thead>
            <tr>
              <th>ID</th><th>Name</th><th>Description</th><th>Active</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((p) => (
              <tr key={p.id}>
                <td>{p.id}</td>
                <td>{p.name}</td>
                <td>{p.description || "-"}</td>
                <td>{p.is_active ? "Yes" : "No"}</td>
                <td>
                  <button onClick={() => setViewItem(p)}>View</button>{" "}
                  <button onClick={() => handleEdit(p)}>Edit</button>{" "}
                  <button onClick={() => handleDelete(p.id)}>Delete</button>
                </td>
              </tr>
            ))}
            {paginated.length === 0 && !loading && (
              <tr><td colSpan="5" style={{ textAlign: "center" }}>No permissions</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        {Array.from({ length: totalPages }).map((_, i) => (
          <button
            key={i + 1}
            onClick={() => setPage(i + 1)}
            className={page === i + 1 ? "active" : ""}
          >
            {i + 1}
          </button>
        ))}
      </div>

      {viewItem && (
        <div className="modal" onClick={() => setViewItem(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>Permission Details</h4>
              <button className="close-button" onClick={() => setViewItem(null)}>✖</button>
            </div>
            <table className="modal-table">
              <tbody>
                <tr><td><strong>ID</strong></td><td>{viewItem.id}</td></tr>
                <tr><td><strong>Name</strong></td><td>{viewItem.name}</td></tr>
                <tr><td><strong>Description</strong></td><td>{viewItem.description || "-"}</td></tr>
                <tr><td><strong>Active</strong></td><td>{viewItem.is_active ? "Yes" : "No"}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
